/**
 * V2 Multi-Roof Builder
 *
 * Transforms component state into the current multi-roof project save payload (schema v2).
 */

import type { ModuleConfig, SnapEdge } from '../projectPersistenceService';
import {
  FREE_FIELD_POLYGON_ID,
  type ProjectStateInput,
  type SolarModuleState,
} from './types';

export interface RoofPolygonV2Data {
  roof_id: number;
  original_polygon_id: number;
  source: 'auto' | 'manual';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  area_m2: number;
  perimeter_m: number;
  is_edited: boolean;
  snap_edge: SnapEdge | null;
}

export interface ObstructionV2Data {
  obstruction_id: string;
  roof_id: number;
  type: 'custom';
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  area_m2: number;
  rotation: number;
}

export interface SegmentV2Data {
  segment_id: number;
  name: string;
  geometry: {
    type: 'Polygon';
    coordinates: number[][][];
  };
  area_m2: number;
  snap_edge_roof_id: number;
  snap_edge_index?: number;
  rotation: number;
  rotation_source: 'snap_edge' | 'auto';
  alignment: 'parallel';
  module_config: ModuleConfig;
  modules: Array<{
    module_id: string;
    position: [number, number];
    width: number;
    height: number;
    orientation: 'portrait' | 'landscape';
    rotation: number;
    row_id: number;
    power_watts: number;
  }>;
  metrics: {
    module_count: number;
    total_area_m2: number;
    coverage_percent: number;
    capacity_kw: number;
  };
}

export interface ProjectDataV2 {
  project_id: string | null;
  rpu?: string;
  schema_version: 2;
  location: {
    address: string;
    coordinates: { lat: number; lng: number };
  };
  cache_references: {
    geotiff_cache_id: string | null;
    building_insights_id: string | null;
    cache_timestamp: string;
  };
  roof_polygons: RoofPolygonV2Data[];
  active_roof_id: number | null;
  obstructions: ObstructionV2Data[];
  segments: SegmentV2Data[];
  default_module_config: ModuleConfig;
  system_metrics: {
    total_roofs: number;
    total_segments: number;
    total_modules: number;
    total_roof_area_m2: number;
    total_segment_area_m2: number;
    roof_coverage_percent: number;
    system_capacity_kw: number;
  };
  status: 'draft' | 'final' | 'archived';
  tags: string[];
  notes: string;
  ui_state: {
    selectedPolygons: number[];
    activeRoofId: number | null;
  };
}

/**
 * Build V2 multi-roof project data from component state
 */
export function buildMultiRoofProjectDataV2(
  state: ProjectStateInput,
  selectedPolygons: Set<number>
): ProjectDataV2 | null {
  const {
    establishedRoofIds,
    polygonData,
    currentProjectId,
    currentRPU,
    currentAddress,
    pinPosition,
    geotiffCacheId,
    buildingInsightsId,
    editedPolygonCoords,
    snapEdgeIndex,
    snapEdgeAzimuth,
    manualAzimuth,
    obstructions,
    modules,
    moduleSegments,
    moduleWidth,
    moduleHeight,
    modulePowerWatts,
    moduleOrientation,
    moduleSpacing,
    rowSpacingMultiplier,
    edgeSetback,
    obstructionSetback,
    activeRoofId,
    projectStatus,
    calculatePolygonArea,
  } = state;

  // Check if we have any data to save (roofs OR free-field segments/obstructions)
  const hasFreeFieldSegments = (moduleSegments[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
  const hasFreeFieldObstructions = (obstructions[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
  const hasFreeFieldModules = (modules[FREE_FIELD_POLYGON_ID]?.length || 0) > 0;
  const hasRoofs = establishedRoofIds.size > 0;

  if (!hasRoofs && !hasFreeFieldSegments && !hasFreeFieldObstructions && !hasFreeFieldModules) {
    console.warn('📦 No data to save - returning null');
    return null;
  }

  // Build roof polygons array
  const roofPolygonsV2: RoofPolygonV2Data[] = [];

  for (const roofId of establishedRoofIds) {
    if (!polygonData?.polygons) {
      console.warn(`📦 No polygonData.polygons available, skipping roof ${roofId}`);
      continue;
    }
    const polygon = polygonData.polygons.find((p) => p.id === roofId);
    if (!polygon) {
      console.warn(`📦 Polygon not found for roof ID ${roofId}`);
      continue;
    }

    const currentCoords = editedPolygonCoords[roofId] ||
      (polygon.geometry.coordinates[0] as [number, number][]).map(([lng, lat]) => [lat, lng] as [number, number]);

    const validCoords = currentCoords.filter(([lat, lng]: [number, number]) => {
      return !isNaN(lat) && !isNaN(lng) && isFinite(lat) && isFinite(lng);
    });

    if (validCoords.length < 3) {
      console.warn(`Roof ${roofId} has insufficient valid coordinates (${validCoords.length})`);
      continue;
    }

    roofPolygonsV2.push({
      roof_id: roofId,
      original_polygon_id: polygon.id,
      source: 'auto',
      geometry: {
        type: 'Polygon',
        coordinates: [validCoords.map(([lat, lng]: [number, number]) => [lng, lat])],
      },
      area_m2: calculatePolygonArea(currentCoords),
      perimeter_m: 0,
      is_edited: editedPolygonCoords[roofId] !== undefined,
      snap_edge: snapEdgeIndex[roofId] !== undefined ? {
        edge_index: snapEdgeIndex[roofId],
        azimuth: snapEdgeAzimuth[roofId] || 0,
        manually_adjusted: manualAzimuth[roofId] !== null,
        coordinates: {
          start: [0, 0] as [number, number],
          end: [0, 0] as [number, number],
        },
      } : null,
    });
  }

  // Build obstructions array
  const obstructionsV2: ObstructionV2Data[] = [];
  for (const roofId of establishedRoofIds) {
    const roofObstructions = obstructions[roofId] || [];
    roofObstructions.forEach((obs, idx) => {
      obstructionsV2.push({
        obstruction_id: `roof${roofId}_obs${String(idx + 1).padStart(3, '0')}`,
        roof_id: roofId,
        type: 'custom',
        geometry: {
          type: 'Polygon',
          coordinates: [obs.map(([lat, lng]) => [lng, lat])],
        },
        area_m2: calculatePolygonArea(obs),
        rotation: 0,
      });
    });
  }

  // Add free-field obstructions (not tied to any roof)
  const freeFieldObstructions = obstructions[FREE_FIELD_POLYGON_ID] || [];
  freeFieldObstructions.forEach((obs, idx) => {
    obstructionsV2.push({
      obstruction_id: `freefield_obs${String(idx + 1).padStart(3, '0')}`,
      roof_id: FREE_FIELD_POLYGON_ID,
      type: 'custom',
      geometry: {
        type: 'Polygon',
        coordinates: [obs.map(([lat, lng]) => [lng, lat])],
      },
      area_m2: calculatePolygonArea(obs),
      rotation: 0,
    });
  });

  // Build segments array
  const segmentsV2: SegmentV2Data[] = [];

  for (const roofId of establishedRoofIds) {
    const roofModules = modules[roofId] || [];
    const roofSegments = moduleSegments[roofId] || [];

    // If we have segments for this roof, use them directly
    if (roofSegments.length > 0) {
      for (const segment of roofSegments) {
        const segmentBounds = segment.bounds || [];
        const segmentRotation = segment.rotation || 0;
        const segmentModules = segment.modules?.length > 0
          ? segment.modules
          : roofModules.filter(m => m.segmentId === segment.id);

        const validModules = segmentModules.filter((m) => {
          const pos = m.position;
          return pos &&
            Array.isArray(pos) &&
            pos.length === 2 &&
            typeof pos[0] === 'number' && !isNaN(pos[0]) &&
            typeof pos[1] === 'number' && !isNaN(pos[1]);
        });

        if (segmentBounds.length >= 3) {
          segmentsV2.push({
            segment_id: segment.id,
            name: `Roof ${roofId} - Segment ${segment.id + 1}`,
            geometry: {
              type: 'Polygon',
              coordinates: [segmentBounds.map(([lat, lng]: [number, number]) => [lng, lat])],
            },
            area_m2: calculatePolygonArea(segmentBounds),
            snap_edge_roof_id: roofId,
            snap_edge_index: snapEdgeIndex[roofId],
            rotation: segmentRotation,
            rotation_source: snapEdgeIndex[roofId] !== undefined ? 'snap_edge' : 'auto',
            alignment: 'parallel',
            module_config: {
              width: moduleWidth,
              height: moduleHeight,
              power_watts: modulePowerWatts,
              orientation: moduleOrientation,
              spacing: moduleSpacing,
              row_spacing_multiplier: rowSpacingMultiplier,
              edge_setback: edgeSetback,
              obstruction_setback: obstructionSetback,
            },
            modules: validModules.map((m, idx) => ({
              module_id: `roof${roofId}_seg${segment.id}_mod${String(idx + 1).padStart(3, '0')}`,
              position: m.position as [number, number],
              width: m.width,
              height: m.height,
              orientation: m.orientation,
              rotation: m.rotation || segmentRotation,
              row_id: 0,
              power_watts: modulePowerWatts,
            })),
            metrics: {
              module_count: validModules.length,
              total_area_m2: validModules.length * moduleWidth * moduleHeight,
              coverage_percent: calculatePolygonArea(segmentBounds) > 0 ? parseFloat(
                ((validModules.length * moduleWidth * moduleHeight) / calculatePolygonArea(segmentBounds) * 100).toFixed(1)
              ) : 0,
              capacity_kw: (validModules.length * modulePowerWatts) / 1000,
            },
          });
        }
      }
      continue;
    }

    // Fallback: Group modules by segmentId if no explicit segments defined
    const segmentMap = new Map<number, SolarModuleState[]>();

    roofModules.forEach((mod) => {
      const segId = mod.segmentId || 0;
      if (!segmentMap.has(segId)) {
        segmentMap.set(segId, []);
      }
      segmentMap.get(segId)!.push(mod);
    });

    for (const [segId, segmentModules] of segmentMap.entries()) {
      const segment = moduleSegments[roofId]?.find((s) => s.id === segId);
      const polygon = polygonData?.polygons?.find((p) => p.id === roofId);
      const currentCoords = editedPolygonCoords[roofId] ||
        (polygon?.geometry.coordinates[0] as [number, number][] | undefined)?.map(([lng, lat]) => [lat, lng] as [number, number]) || [];
      const segmentBounds = segment?.bounds || currentCoords;
      const segmentRotation = segment?.rotation || 0;

      const validModules = segmentModules.filter((m) => {
        const pos = m.position;
        return pos &&
          Array.isArray(pos) &&
          pos.length === 2 &&
          typeof pos[0] === 'number' && !isNaN(pos[0]) &&
          typeof pos[1] === 'number' && !isNaN(pos[1]);
      });

      segmentsV2.push({
        segment_id: segId,
        name: `Roof ${roofId} - Segment ${segId + 1}`,
        geometry: {
          type: 'Polygon',
          coordinates: [segmentBounds.map(([lat, lng]: [number, number]) => [lng, lat])],
        },
        area_m2: calculatePolygonArea(segmentBounds),
        snap_edge_roof_id: roofId,
        snap_edge_index: snapEdgeIndex[roofId],
        rotation: segmentRotation,
        rotation_source: snapEdgeIndex[roofId] !== undefined ? 'snap_edge' : 'auto',
        alignment: 'parallel',
        module_config: {
          width: moduleWidth,
          height: moduleHeight,
          power_watts: modulePowerWatts,
          orientation: moduleOrientation,
          spacing: moduleSpacing,
          row_spacing_multiplier: rowSpacingMultiplier,
          edge_setback: edgeSetback,
          obstruction_setback: obstructionSetback,
        },
        modules: validModules.map((m, idx) => ({
          module_id: `roof${roofId}_seg${segId}_mod${String(idx + 1).padStart(3, '0')}`,
          position: m.position,
          width: m.width,
          height: m.height,
          orientation: m.orientation,
          rotation: m.rotation || segmentRotation,
          row_id: 0,
          power_watts: modulePowerWatts,
        })),
        metrics: {
          module_count: validModules.length,
          total_area_m2: validModules.length * moduleWidth * moduleHeight,
          coverage_percent: calculatePolygonArea(segmentBounds) > 0 ? parseFloat(
            ((validModules.length * moduleWidth * moduleHeight) / calculatePolygonArea(segmentBounds) * 100).toFixed(1)
          ) : 0,
          capacity_kw: (validModules.length * modulePowerWatts) / 1000,
        },
      });
    }
  }

  // Add free-field segments (not tied to any roof)
  const freeFieldSegments = moduleSegments[FREE_FIELD_POLYGON_ID] || [];
  const freeFieldModulesArray = modules[FREE_FIELD_POLYGON_ID] || [];

  freeFieldSegments.forEach((segment, segIdx) => {
    const segmentBounds = segment.bounds || [];
    const segmentRotation = segment.rotation || 0;
    const segmentModules = segment.modules?.length > 0
      ? segment.modules
      : freeFieldModulesArray.filter(m => m.segmentId === segment.id);

    const validModules = segmentModules.filter((m) => {
      const pos = m.position;
      return pos &&
        Array.isArray(pos) &&
        pos.length === 2 &&
        typeof pos[0] === 'number' && !isNaN(pos[0]) &&
        typeof pos[1] === 'number' && !isNaN(pos[1]);
    });

    if (segmentBounds.length >= 3) {
      segmentsV2.push({
        segment_id: segment.id,
        name: `Field Segment ${segIdx + 1}`,
        geometry: {
          type: 'Polygon',
          coordinates: [segmentBounds.map(([lat, lng]: [number, number]) => [lng, lat])],
        },
        area_m2: calculatePolygonArea(segmentBounds),
        snap_edge_roof_id: FREE_FIELD_POLYGON_ID,
        snap_edge_index: undefined,
        rotation: segmentRotation,
        rotation_source: 'auto',
        alignment: 'parallel',
        module_config: {
          width: moduleWidth,
          height: moduleHeight,
          power_watts: modulePowerWatts,
          orientation: moduleOrientation,
          spacing: moduleSpacing,
          row_spacing_multiplier: rowSpacingMultiplier,
          edge_setback: edgeSetback,
          obstruction_setback: obstructionSetback,
        },
        modules: validModules.map((m, idx) => ({
          module_id: `freefield_seg${segment.id}_mod${String(idx + 1).padStart(3, '0')}`,
          position: m.position as [number, number],
          width: m.width,
          height: m.height,
          orientation: m.orientation,
          rotation: m.rotation || segmentRotation,
          row_id: 0,
          power_watts: modulePowerWatts,
        })),
        metrics: {
          module_count: validModules.length,
          total_area_m2: validModules.length * moduleWidth * moduleHeight,
          coverage_percent: calculatePolygonArea(segmentBounds) > 0 ? parseFloat(
            ((validModules.length * moduleWidth * moduleHeight) / calculatePolygonArea(segmentBounds) * 100).toFixed(1)
          ) : 0,
          capacity_kw: (validModules.length * modulePowerWatts) / 1000,
        },
      });
    }
  });

  // Calculate aggregated system metrics (now includes free-field segments)
  const totalModules = segmentsV2.reduce((sum, seg) => sum + seg.metrics.module_count, 0);
  const totalRoofArea = roofPolygonsV2.reduce((sum, roof) => sum + roof.area_m2, 0);
  const totalSegmentArea = segmentsV2.reduce((sum, seg) => sum + seg.area_m2, 0);
  const systemCapacityKw = (totalModules * modulePowerWatts) / 1000;

  return {
    project_id: currentProjectId,
    rpu: currentRPU || undefined,
    schema_version: 2,
    location: {
      address: currentAddress || '',
      coordinates: {
        lat: pinPosition?.lat || 0,
        lng: pinPosition?.lng || 0,
      },
    },
    cache_references: {
      geotiff_cache_id: geotiffCacheId,
      building_insights_id: buildingInsightsId,
      cache_timestamp: new Date().toISOString(),
    },
    roof_polygons: roofPolygonsV2,
    active_roof_id: activeRoofId,
    obstructions: obstructionsV2,
    segments: segmentsV2,
    default_module_config: {
      width: moduleWidth,
      height: moduleHeight,
      power_watts: modulePowerWatts,
      orientation: moduleOrientation,
      spacing: moduleSpacing,
      row_spacing_multiplier: rowSpacingMultiplier,
      edge_setback: edgeSetback,
      obstruction_setback: obstructionSetback,
    },
    system_metrics: {
      total_roofs: roofPolygonsV2.length,
      total_segments: segmentsV2.length,
      total_modules: totalModules,
      total_roof_area_m2: totalRoofArea,
      total_segment_area_m2: totalSegmentArea,
      roof_coverage_percent: totalRoofArea > 0 ? parseFloat(
        ((totalModules * moduleWidth * moduleHeight) / totalRoofArea * 100).toFixed(1)
      ) : 0,
      system_capacity_kw: systemCapacityKw,
    },
    status: projectStatus,
    tags: [],
    notes: '',
    ui_state: {
      selectedPolygons: Array.from(selectedPolygons),
      activeRoofId: activeRoofId,
    },
  };
}
